<template>
  <b-card>
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="categoryData === undefined"
    >
      <h4 class="alert-heading">
        Error fetching user data
      </h4>
      <div class="alert-body">
        No user found with this user id. Check
        <b-link
          class="alert-link"
          :to="{ name: 'app-admin-category'}"
        >
          User List
        </b-link>
        for other users.
      </div>
    </b-alert>

    <template v-if="categoryData">
      <!-- First Row -->
      <b-row>
        <b-col
            cols="12"
            xl="12"
            lg="12"
            md="12"
            class="text-center"
        >

            <h1>{{ categoryData.nome }}</h1>

            <p>{{ categoryData.descricao}}</p>

        </b-col>
        <b-col
          cols="12"
          xl="12"
          lg="12"
          md="12"
        >
          <div>
            <!-- Table Container Card -->
            <b-card
              no-body
              class="mb-0"
            >
              <b-table
                ref="refTableItemList"
                class="position-relative"
                :items="categoryData.items"
                responsive
                :fields="tableColumns"
                primary-key="id"
                show-empty
                empty-text="Nenhum registro encontrado"
              >

                <template #cell(item_nome)="data">
                  <b-media
                    vertical-align="center"
                  >
                    <template #aside>
                      <b-avatar
                        size="32"
                        :text="avatarText(data.item.item_nome)"
                        :src="data.item.imagens.length > 0 ? data.item.imagens[0].caminho_imagem : null"
                      />
                    </template>
                    <span class="font-weight-bold d-block text-nowrap">
                      {{
                        data.item.item_nome.length > 30 ? data.item.item_nome.substring(0, 30) + '...' : data.item.item_nome
                      }}
                    </span>
                  </b-media>
                </template>

                <template #cell(price)="data">
                  <span
                    v-if="data.item.tabela_preco_itens.length > 0"
                    class="text-nowrap"
                  >
                    {{ data.item.tabela_preco_itens[0].preco_venda | currency }}
                  </span>
                  <span
                    v-else
                    class="text-nowrap"
                  >
                    {{ data.item.preco_venda | currency }}
                  </span>
                </template>

                <template #cell(data_inclusao)="data">
                  <span class="text-nowrap">
                    {{ data.item.data_inclusao | formatDateTimeBr }}
                  </span>
                </template>

                <!-- Column: Id -->
                <template #cell(id)="data">
                  <router-link :to="{ name: 'app-adm-produto-add', params: { id: data.item.id }}" class="text-bold">
                    #{{ data.item.id }}
                  </router-link>
                </template>

                <template #cell(item_descricao)="data">
                  <span v-html="data.value" />
                </template>

                <!-- Column: Status -->
                <template #cell(ativo)="data">
                  <b-badge
                    pill
                    :variant="`light-${resolveUserStatusVariant(data.item.ativo)}`"
                    class="text-capitalize"
                  >
                    {{ data.item.ativo === 1 ? 'Ativo' : data.item.ativo === 0 ? 'Inativo' : 'Deletado' }}
                  </b-badge>
                </template>

              </b-table>
              <div class="mx-2 mb-2">
                <b-row>

                  <b-col
                    cols="12"
                    sm="6"
                    class="d-flex align-items-center justify-content-center justify-content-sm-start"
                  >
                    <span class="text-muted">Mostrando {{ dataMeta.from }} para {{ dataMeta.to }} de {{
                      dataMeta.of
                    }} entradas</span>
                  </b-col>
                  <!-- Pagination -->
                  <b-col
                    cols="12"
                    sm="6"
                    class="d-flex align-items-center justify-content-center justify-content-sm-end"
                  >

                    <b-pagination
                      v-model="currentPage"
                      :total-rows="categoryData.items.length"
                      :per-page="perPage"
                      first-number
                      last-number
                      class="mb-0 mt-1 mt-sm-0"
                      prev-class="prev-item"
                      next-class="next-item"
                    >
                      <template #prev-text>
                        <feather-icon
                          icon="ChevronLeftIcon"
                          size="18"
                        />
                      </template>
                      <template #next-text>
                        <feather-icon
                          icon="ChevronRightIcon"
                          size="18"
                        />
                      </template>
                    </b-pagination>

                  </b-col>

                </b-row>
              </div>
            </b-card>
          </div>
        </b-col>

      </b-row>

    </template>

  </b-card>
</template>

<script>
import store from '@/store'
import router from '@/router'
import { computed, onMounted, ref } from '@vue/composition-api'
import { avatarText, formatDateTimeBr, currency } from '@/@core/utils/filter'
import useProdutoList from '@/views/apps/produtos/list/useProdutosList'
import {
  BRow, BCol, BAlert, BLink, BTable, BPagination, BCard, BAvatar, BMedia, BBadge,
} from 'bootstrap-vue'

export default {
  components: {
    BRow,
    BCol,
    BAlert,
    BLink,
    BTable,
    BPagination,
    BCard,
    BAvatar,
    BMedia,
    BBadge,
    // Local Components
  },
  filters: {
    formatDateTimeBr,
    currency,
  },
  setup() {
    const categoryData = ref(null)
    const refTableItemList = ref(null)
    const perPage = ref(100)
    const currentPage = ref(1)

    const tableColumns = [
      { key: 'id', sortable: true, label: '#' },
      { key: 'item_nome', sortable: true, label: 'Nome' },
      { key: 'price', sortable: true, label: 'Preço Venda' },
      { key: 'ativo', sortable: true, label: 'Status' },
      { key: 'data_inclusao', sortable: true, label: 'Data criação' },
    ]

    const dataMeta = computed(() => {
      const localItemsCount = 0
      return {
        from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: categoryData.value.items.length,
      }
    })

    onMounted(() => {
      store.dispatch('appAdmCategory/fetchCategory', { id: router.currentRoute.params.id })
        .then(response => {
          categoryData.value = response.data
        })
        .catch(error => {
          if (error.response.status === 404) {
            categoryData.value = undefined
          }
        })
    })

    const {
      resolveUserStatusVariant,
    } = useProdutoList()

    return {
      tableColumns,
      categoryData,
      dataMeta,
      refTableItemList,
      avatarText,
      resolveUserStatusVariant,

      perPage,
      currentPage,
    }
  },
}
</script>

<style>

</style>
